import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  LogIn,
  Search,
} from 'lucide-react';
import { useTutorialAuth } from '@/context/TutorialAuthContext';
import { tutorialAPI } from '@/utils/tutorialApi';
import TutorialUserMenu from '@/pages/tutorial/components/TutorialUserMenu';
import IpeLocaleSwitcher from '@/i18n/IpeLocaleSwitcher';
import TutorialProfileModal from '@/pages/tutorial/components/TutorialProfileModal';
import {
  IPE_MANUAL_HOME_PATH,
  IPE_MANUAL_LOGIN_PATH,
  buildManualNodePath,
} from '@/config/ipeRoutes';
import '@/styles/tutorial/tutorial-manual.css';

function TreeNode({ node, activeSlug, expanded, onToggle, depth = 0 }) {
  const isFolder = node.nodeType === 'FOLDER';
  const isActive = node.slug === activeSlug;
  const isExpanded = expanded.has(node.slug);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  return (
    <div className="tutorial-tree__branch">
      <div
        className={`tutorial-tree__row${isActive ? ' tutorial-tree__row--active' : ''}`}
        style={{ paddingLeft: `${12 + depth * 14}px` }}
      >
        {isFolder && hasChildren ? (
          <button
            type="button"
            className="tutorial-tree__toggle"
            onClick={() => onToggle(node.slug)}
            aria-label={isExpanded ? '折叠' : '展开'}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="tutorial-tree__toggle tutorial-tree__toggle--placeholder" />
        )}
        <Link to={buildManualNodePath(node.slug)} className="tutorial-tree__link">
          {isFolder ? <Folder size={15} /> : <FileText size={15} />}
          <span>{node.title}</span>
        </Link>
      </div>
      {isFolder && hasChildren && isExpanded ? (
        <div className="tutorial-tree__children">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              activeSlug={activeSlug}
              expanded={expanded}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function findPathSlugs(nodes, targetSlug, trail = []) {
  for (const node of nodes || []) {
    const nextTrail = [...trail, node.slug];
    if (node.slug === targetSlug) return nextTrail;
    if (node.children?.length) {
      const found = findPathSlugs(node.children, targetSlug, nextTrail);
      if (found) return found;
    }
  }
  return null;
}

export default function TutorialManualLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isEditor, isManager, editorApplyStatus, applyEditor, updateProfile, logout } = useTutorialAuth();
  const [tree, setTree] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [loadingTree, setLoadingTree] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyReason, setApplyReason] = useState('');
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [applySuccess, setApplySuccess] = useState('');

  const activeSlug = useMemo(() => {
    const match = location.pathname.match(/\/ipe\/manual\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }, [location.pathname]);

  const loadTree = useCallback(async () => {
    setLoadingTree(true);
    try {
      const data = await tutorialAPI.getTree();
      const nextTree = data?.tree || [];
      setTree(nextTree);
      setExpanded((prev) => {
        const merged = new Set(prev);
        if (activeSlug) {
          const path = findPathSlugs(nextTree, activeSlug);
          path?.forEach((slug) => merged.add(slug));
        }
        return merged;
      });
    } catch {
      setTree([]);
    } finally {
      setLoadingTree(false);
    }
  }, [activeSlug]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  useEffect(() => {
    document.title = 'IPE 使用手册';
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      rootOverflow: root?.style.overflow ?? '',
      rootHeight: root?.style.height ?? '',
    };

    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    if (root) {
      root.style.overflow = 'hidden';
      root.style.height = '100%';
    }

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  useEffect(() => {
    const keyword = searchText.trim();
    if (!keyword) {
      setSearchResults([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        const data = await tutorialAPI.search(keyword);
        setSearchResults(data?.items || []);
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const onToggle = useCallback((slug) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  const handleLogout = () => {
    logout();
    navigate(IPE_MANUAL_HOME_PATH);
  };

  const openApplyModal = () => {
    setApplyReason('');
    setApplyError('');
    setApplySuccess('');
    setApplyOpen(true);
  };

  const closeApplyModal = () => {
    if (applySubmitting) return;
    setApplyOpen(false);
  };

  const handleApplyEditor = async () => {
    setApplyError('');
    setApplySuccess('');
    setApplySubmitting(true);
    try {
      await applyEditor(applyReason.trim());
      setApplySuccess('申请已提交，请等待管理员审核');
      window.setTimeout(() => {
        setApplyOpen(false);
        setApplySuccess('');
      }, 1200);
    } catch (err) {
      setApplyError(err.message || '申请失败');
    } finally {
      setApplySubmitting(false);
    }
  };

  const canApplyEditor = isAuthenticated && !isEditor && editorApplyStatus !== 'PENDING';
  const applyLabel = editorApplyStatus === 'REJECTED' ? '重新申请编辑' : '申请编辑权限';

  return (
    <div className="tutorial-manual">
      <aside className="tutorial-manual__sidebar">
        <div className="tutorial-manual__brand">
          <BookOpen size={20} />
          <div>
            <strong>IPE 使用手册</strong>
            <span>Product Guide</span>
          </div>
        </div>
        <div className="tutorial-manual__search">
          <Search size={16} />
          <input
            type="search"
            className="tutorial-manual__search-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="搜索知识库"
          />
        </div>
        {searchText.trim() ? (
          <div className="tutorial-manual__search-results">
            {searchResults.length === 0 ? (
              <p className="tutorial-manual__empty">未找到相关内容</p>
            ) : (
              searchResults.map((item) => (
                <Link
                  key={item.id}
                  to={buildManualNodePath(item.slug)}
                  className="tutorial-manual__search-item"
                  onClick={() => setSearchText('')}
                >
                  <FileText size={14} />
                  <span>{item.title}</span>
                </Link>
              ))
            )}
          </div>
        ) : (
          <nav className="tutorial-tree">
            <Link
              to={IPE_MANUAL_HOME_PATH}
              className={`tutorial-tree__home${!activeSlug ? ' tutorial-tree__row--active' : ''}`}
            >
              <BookOpen size={15} />
              <span>手册首页</span>
            </Link>
            {loadingTree ? (
              <p className="tutorial-manual__empty">加载目录中…</p>
            ) : (
              tree.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  activeSlug={activeSlug}
                  expanded={expanded}
                  onToggle={onToggle}
                />
              ))
            )}
          </nav>
        )}
      </aside>

      <div className="tutorial-manual__main">
        <header className="tutorial-manual__header">
          <div className="tutorial-manual__header-left">
            <Link to={IPE_MANUAL_HOME_PATH} className="tutorial-manual__home-link">IPE</Link>
            <span className="tutorial-manual__header-sep">/</span>
            <span>使用手册</span>
          </div>
          <div className="tutorial-manual__header-actions">
            <span data-ipe-locale-dock="">
              <IpeLocaleSwitcher />
            </span>
            {isAuthenticated ? (
              <TutorialUserMenu
                user={user}
                isEditor={isEditor}
                isManager={isManager}
                editorApplyStatus={editorApplyStatus}
                canApplyEditor={canApplyEditor}
                applyLabel={applyLabel}
                onOpenProfile={() => setProfileOpen(true)}
                onOpenApply={openApplyModal}
                onLogout={handleLogout}
              />
            ) : (
              <Link to={IPE_MANUAL_LOGIN_PATH} className="tutorial-manual__primary-btn">
                <LogIn size={15} />
                登录 / 注册
              </Link>
            )}
          </div>
        </header>
        <div className="tutorial-manual__content">
          <Outlet context={{ reloadTree: loadTree, isEditor, isManager, user }} />
        </div>
      </div>

      {profileOpen ? (
        <TutorialProfileModal
          user={user}
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          onSave={updateProfile}
        />
      ) : null}

      {applyOpen ? (
        <div className="tutorial-apply-backdrop" role="presentation">
          <div className="tutorial-apply-modal" role="dialog" aria-modal="true" aria-labelledby="tutorial-apply-title">
            <h3 id="tutorial-apply-title">申请编辑权限</h3>
            <p className="tutorial-apply-modal__hint">
              提交后由管理员在后台审核，通过后即可编辑教程内容。
            </p>
            <label className="tutorial-apply-modal__field">
              申请说明（可选）
              <textarea
                value={applyReason}
                onChange={(e) => setApplyReason(e.target.value)}
                placeholder="简要说明申请编辑权限的原因"
                rows={4}
                maxLength={500}
              />
            </label>
            {applyError ? <p className="tutorial-apply-modal__error">{applyError}</p> : null}
            {applySuccess ? <p className="tutorial-apply-modal__success">{applySuccess}</p> : null}
            <div className="tutorial-apply-modal__actions">
              <button type="button" className="tutorial-manual__ghost-btn" disabled={applySubmitting} onClick={closeApplyModal}>
                取消
              </button>
              <button type="button" className="tutorial-manual__primary-btn" disabled={applySubmitting} onClick={handleApplyEditor}>
                {applySubmitting ? '提交中…' : '提交申请'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
